import React, { useEffect, useState } from "react";
import { Badge, Form } from "react-bootstrap";
import { css } from "@emotion/css";
import {
  useV2ListClientLocationsForClientQuery,
  useV2ListContactsForClientQuery,
  useV2ListCrmContactsQuery,
  useV2ListCrmSitesQuery,
} from "@generated";
import useFlags from "@common/hooks/useFlags";
import useTheme from "@common/hooks/useTheme";
// Same dropdown look as the AutoCompleteText name suggestions.
import "@components/Forms/AutoCompleteText/AutoCompleteText.css";

export type PickedRecord =
  | { source: "client"; id: string; fields: Record<string, string | undefined> }
  | {
      source: "crm";
      crmId: string;
      fields: Record<string, string | undefined>;
    };

interface Props {
  kind: "site" | "contact";
  projectId: string;
  clientId?: string | null;
  onPick: (record: PickedRecord) => void;
}

interface Option {
  key: string;
  label: string;
  detail: string;
  onClient: boolean;
  record: PickedRecord;
}

const DEBOUNCE_MS = 300;
const MIN_CRM_QUERY = 2;
export const CRM_UNAVAILABLE_MESSAGE =
  "ConnectWise is unavailable right now — enter the details manually.";

// Wire shape shared by client-location, contact and crm-* rows at the API
// boundary. Keys are kebab-case on the wire.
type WireAttributes = Record<string, string | boolean | null | undefined>;
type WireRow = { id?: string | number; attributes?: WireAttributes };

const str = (value: WireAttributes[string]): string | undefined =>
  value == null || value === "" ? undefined : String(value);

const siteFields = (a: WireAttributes = {}) => ({
  name: str(a.name),
  street: str(a.street),
  street2: str(a.street2),
  city: str(a.city),
  state: str(a.state),
  postalCode: str(a["postal-code"]),
  country: str(a.country),
});

const contactFields = (a: WireAttributes = {}) => ({
  name: str(a.name),
  title: str(a.title),
  email: str(a.email),
  phone: str(a.phone),
});

const describe = (
  kind: Props["kind"],
  fields: Record<string, string | undefined>
): string =>
  (kind === "site"
    ? [fields.street, fields.city, fields.state]
    : [fields.title, fields.email]
  )
    .filter(Boolean)
    .join(", ");

// 404 crm_unavailable: no ConnectWise company for this client, or ConnectWise
// rejected the read. Not an outage — just hide the group. Any other error
// (502, other 404s) shows the manual-entry hint.
type ApiError = {
  status?: unknown;
  data?: { errors?: { code?: string }[] };
};
const isCrmHidden = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { status, data } = error as ApiError;
  return status === 404 && data?.errors?.[0]?.code === "crm_unavailable";
};

const useStyles = () => {
  const theme = useTheme();
  return {
    list: css`
      max-height: 320px;
      overflow-y: auto;
      margin-bottom: 0;
    `,
    groupHeader: css`
      cursor: default !important;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      color: ${theme.colors.navy["1"]};
      background-color: #f8f9fa;
      &:hover {
        background-color: #f8f9fa !important;
      }
    `,
    detail: css`
      display: block;
      font-size: 12px;
      color: #6c757d;
    `,
    badge: css`
      margin-left: 8px;
    `,
    unavailable: css`
      font-size: 12px;
      color: #6c757d;
      margin-top: 4px;
    `,
  };
};

const ClientRecordPicker = ({
  kind,
  projectId,
  clientId,
  onPick,
}: Props): JSX.Element | null => {
  const { releaseClientLocations } = useFlags();
  const styles = useStyles();

  const [term, setTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [crmQuery, setCrmQuery] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  // First request goes without q; afterwards only search at >= 2 chars (the
  // API 400s shorter terms). A one-character term keeps the previous result.
  useEffect(() => {
    if (debouncedTerm.length === 0) setCrmQuery(undefined);
    else if (debouncedTerm.length >= MIN_CRM_QUERY) setCrmQuery(debouncedTerm);
  }, [debouncedTerm]);

  const clientFilter = {
    active: true,
    ...(debouncedTerm ? { name: debouncedTerm } : {}),
  };
  // No client means no CW company mapping either: nothing to search.
  const disabled = !releaseClientLocations || !clientId;
  const crmArgs = {
    projectId,
    ...(crmQuery ? { filter: { q: crmQuery } } : {}),
  };

  const clientLocations = useV2ListClientLocationsForClientQuery(
    { clientId: clientId as string, filter: clientFilter },
    { skip: disabled || kind !== "site" }
  );
  const clientContacts = useV2ListContactsForClientQuery(
    { clientId: clientId as string, filter: clientFilter },
    { skip: disabled || kind !== "contact" }
  );
  const crmSites = useV2ListCrmSitesQuery(crmArgs, {
    skip: disabled || kind !== "site",
  });
  const crmContacts = useV2ListCrmContactsQuery(crmArgs, {
    skip: disabled || kind !== "contact",
  });

  if (disabled) return null;

  const toFields = kind === "site" ? siteFields : contactFields;
  const clientResult = kind === "site" ? clientLocations : clientContacts;
  const crmResult = kind === "site" ? crmSites : crmContacts;
  const localIdKey = kind === "site" ? "client-location-id" : "contact-id";

  const clientRows = (clientResult.data?.data ?? []) as unknown as WireRow[];
  const clientOptions: Option[] = clientRows.map((row) => {
    const fields = toFields(row.attributes);
    return {
      key: `client-${row.id}`,
      label: fields.name ?? "",
      detail: describe(kind, fields),
      onClient: false,
      record: { source: "client", id: String(row.id), fields },
    };
  });

  const crmHidden = isCrmHidden(crmResult.error);
  const crmUnavailable = !!crmResult.error && !crmHidden;
  const crmRows = (crmResult.error
    ? []
    : crmResult.data?.data ?? []) as unknown as WireRow[];
  // A CW row already adopted onto a client record listed above would just
  // repeat it; drop it. Keep server order: the API puts primary rows first.
  const shownClientIds = new Set(clientRows.map((row) => String(row.id)));
  const crmOptions: Option[] = crmRows.flatMap((row) => {
    const fields = toFields(row.attributes);
    const localId = str(row.attributes?.[localIdKey]);
    if (localId && shownClientIds.has(localId)) return [];
    return {
      key: `crm-${row.id}`,
      label: fields.name ?? "",
      detail: describe(kind, fields),
      onClient: !!localId,
      record: localId
        ? { source: "client", id: localId, fields }
        : { source: "crm", crmId: String(row.id), fields },
    };
  });

  const select = (option: Option) => {
    onPick(option.record);
    setTerm("");
    setOpen(false);
  };

  const renderGroup = (title: string, options: Option[], testId: string) =>
    options.length === 0 ? null : (
      <React.Fragment key={title}>
        <li className={styles.groupHeader} role="presentation">
          {title}
        </li>
        {options.map((option) => (
          <li
            key={option.key}
            role="option"
            aria-selected={false}
            data-testid={testId}
            // Keep focus on the input so blur doesn't close the list first.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => select(option)}
          >
            <span>{option.label}</span>
            {option.onClient && (
              <Badge bg="secondary" className={styles.badge}>
                On client
              </Badge>
            )}
            {option.detail && (
              <span className={styles.detail}>{option.detail}</span>
            )}
          </li>
        ))}
      </React.Fragment>
    );

  const hasOptions = clientOptions.length > 0 || crmOptions.length > 0;
  const noun = kind === "site" ? "location" : "contact";

  return (
    <Form.Group className="formGroup">
      <Form.Label htmlFor={`client-record-picker-${kind}`}>
        {`Find a saved ${noun}`}
      </Form.Label>
      <Form.Control
        id={`client-record-picker-${kind}`}
        role="combobox"
        aria-expanded={open && hasOptions}
        autoComplete="off"
        placeholder={`Search client ${noun}s${
          crmHidden ? "" : " and ConnectWise"
        }`}
        value={term}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          // Enter here must not submit the surrounding form.
          if (e.key === "Enter") e.preventDefault();
        }}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
      />
      {open && hasOptions && (
        <ul
          role="listbox"
          className={`autoCompleteList visible ${styles.list}`}
        >
          {renderGroup(
            kind === "site" ? "Client locations" : "Client contacts",
            clientOptions,
            "client-row"
          )}
          {renderGroup("ConnectWise", crmOptions, "crm-row")}
        </ul>
      )}
      {crmUnavailable && (
        <div className={styles.unavailable}>{CRM_UNAVAILABLE_MESSAGE}</div>
      )}
    </Form.Group>
  );
};

export default ClientRecordPicker;
